import { useCallback, useEffect, useRef, useState, type FC } from "react";

import { ArrowRight, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { cn, EventType, setPersonProperties, trackEvent } from "@/shared/utils/core";
import { getFileTypes, secondsSince } from "@/shared/utils/funnelAnalytics";
import { invalidateUploadedDocumentQueries } from "@/shared/utils/cacheInvalidation";
import { useStepAnalytics } from "@/shared/hooks/useStepAnalytics";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useDocumentDelete } from "@/shared/hooks/useDocumentStorage";
import { useMarkIntakeStep } from "@/shared/hooks/useMarkIntakeStep";
import { INTAKE_TASK_IDS } from "@/shared/constants/taskIds";
import { useTriageResume, type TriageStep } from "../hooks/useTriageResume";
import { TriageModalStepperStep } from "./TriageModalStepperStep";
import { TriageModalDeathCertificateStep } from "./TriageModalDeathCertificateStep";
import { TriageModalQrStep } from "./TriageModalQrStep";
import { TriageModalRequestStep } from "./TriageModalRequestStep";
import { TriageModalSidebar } from "./TriageModalSidebar";
import { TriageModalScanningStep } from "./TriageModalScanningStep";
import { TriageModalConfirmDetailsStep } from "./TriageModalConfirmDetailsStep";
import { TriageModalAuthorityStep } from "./TriageModalAuthorityStep";
import { TriageModalInviteStep } from "./TriageModalInviteStep";
import { TriageModalProcessingStep } from "./TriageModalProcessingStep";

import type { AuthorityInviteData } from "./TriageModalAuthorityStep";
import type { CompletedUpload } from "./TriageModalDeathCertificateStep";
import type { ConfirmDetailsResult } from "./TriageModalConfirmDetailsStep";
import type { ExtractionResult } from "./TriageModalScanningStep";

const TRIAGE_STEP_NUMBERS: Record<TriageStep, number> = {
  welcome: 1,
  "death-certificate": 2,
  qr: 3,
  request: 4,
  scanning: 5,
  "confirm-details": 6,
  authority: 7,
  invite: 8,
  processing: 9,
};

type TriageOutcome = "full" | "death_certificate_requested";

interface TriageModalProps {
  open: boolean;
  openSource?: "auto" | "finish_setup";
  firstName: string;
  personFirstName: string;
  onComplete: () => void;
  onDismiss?: () => void;
}

export const TriageModal: FC<TriageModalProps> = ({
  open,
  openSource = "auto",
  firstName,
  personFirstName,
  onComplete,
  onDismiss,
}) => {
  const person = usePerson();
  const estateId = person?.estate_id;
  const queryClient = useQueryClient();
  const markStep = useMarkIntakeStep();
  const {
    step,
    setStep,
    existingDeathCerts,
    refetchDeathCerts,
    requestedFrom: resumedRequestedFrom,
    isReady,
    isResumeResolved,
    isResumed,
  } = useTriageResume(estateId, open);
  const isTracking = open && isReady && isResumeResolved;
  const analyticsEstateId = estateId != null ? String(estateId) : undefined;

  const { trackStepBack, trackFinalStepCompleted, skipNextCompletion } = useStepAnalytics({
    stepId: isTracking ? step : null,
    stepNumber: TRIAGE_STEP_NUMBERS[step],
    events: {
      viewed: EventType.TRIAGE_STEP_VIEWED,
      completed: EventType.TRIAGE_STEP_COMPLETED,
      back: EventType.TRIAGE_STEP_BACK_CLICKED,
    },
    properties: { estate_id: analyticsEstateId },
    isResume: isResumed,
  });

  const wasTrackingRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const uploadMethodRef = useRef<"computer" | "phone" | null>(null);
  const extractionResultRef = useRef<ExtractionResult | undefined>(undefined);
  const authorityBucketRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isTracking && !wasTrackingRef.current) {
      trackEvent(EventType.TRIAGE_OPENED, {
        estate_id: analyticsEstateId,
        open_source: openSource,
        is_resume: isResumed,
        step_id: step,
      });
      startedAtRef.current = startedAtRef.current ?? Date.now();
    }
    wasTrackingRef.current = isTracking;
  }, [isTracking, analyticsEstateId, openSource, isResumed, step]);

  const [sessionUploads, setSessionUploads] = useState<CompletedUpload[]>([]);
  const [removedDocIds, setRemovedDocIds] = useState<Set<number>>(new Set());
  const [removedSessionFileNames, setRemovedSessionFileNames] = useState<Set<string>>(new Set());
  const [requestedFrom, setRequestedFrom] = useState<string | null>(null);
  const hasRestoredRequestRef = useRef(false);
  useEffect(() => {
    if (isResumeResolved && resumedRequestedFrom && !hasRestoredRequestRef.current) {
      hasRestoredRequestRef.current = true;
      setRequestedFrom(resumedRequestedFrom);
    }
    if (!open) {
      hasRestoredRequestRef.current = false;
    }
  }, [isResumeResolved, resumedRequestedFrom, open]);
  const [inviteQueue, setInviteQueue] = useState<AuthorityInviteData[]>([]);
  const [inviteIndex, setInviteIndex] = useState(0);
  const sentInviteNames = useRef<Set<string>>(new Set());

  const inviteKey = (d: AuthorityInviteData) => d.inviteeName.trim().toLowerCase();
  const isSent = (d: AuthorityInviteData) => sentInviteNames.current.has(inviteKey(d));

  const findUnsentIndex = (queue: AuthorityInviteData[], from: number, direction: 1 | -1) => {
    for (let i = from; direction === 1 ? i < queue.length : i >= 0; i += direction) {
      if (!isSent(queue[i])) return i;
    }
    return -1;
  };

  const completedUploads: CompletedUpload[] = [
    ...(existingDeathCerts ?? []).filter((f) =>
      f.documentId ? !removedDocIds.has(f.documentId) : !removedSessionFileNames.has(f.fileName),
    ),
    ...sessionUploads.filter((f) =>
      f.documentId ? !removedDocIds.has(f.documentId) : !removedSessionFileNames.has(f.fileName),
    ),
  ];

  const completeTriage = (outcome: TriageOutcome) => {
    trackFinalStepCompleted();
    trackEvent(EventType.TRIAGE_COMPLETED, {
      estate_id: analyticsEstateId,
      outcome,
      upload_method: uploadMethodRef.current ?? "none",
      authority_bucket: authorityBucketRef.current,
      invites_sent_count: sentInviteNames.current.size,
      total_duration_seconds:
        startedAtRef.current !== null ? secondsSince(startedAtRef.current) : undefined,
    });
    setPersonProperties({}, { triage_completed_at: new Date().toISOString() });
    onComplete();
  };

  const handleDismiss = onDismiss
    ? () => {
        trackEvent(EventType.TRIAGE_DISMISSED, {
          estate_id: analyticsEstateId,
          step_id: step,
          dismiss_method: "close_button",
        });
        skipNextCompletion();
        onDismiss();
      }
    : undefined;

  const trackMethodSelected = (method: "phone" | "request") =>
    trackEvent(EventType.DEATH_CERTIFICATE_METHOD_SELECTED, {
      estate_id: analyticsEstateId,
      method,
    });

  const handleStart = () => {
    trackEvent(EventType.TRIAGE_STARTED, { estate_id: analyticsEstateId, is_resume: isResumed });
    startedAtRef.current = Date.now();
    markStep.mutate({ taskId: INTAKE_TASK_IDS.INTRO, status: "in_progress" });
    setStep("death-certificate");
  };
  const goBackTo = (target: TriageStep) => {
    trackStepBack();
    setStep(target);
  };
  const handleBack = () => goBackTo("welcome");
  const handlePhoneUpload = () => {
    trackMethodSelected("phone");
    setStep("qr");
  };
  const handleRequestUpload = () => {
    trackMethodSelected("request");
    setStep("request");
  };
  const handleQrBack = () => goBackTo("death-certificate");
  const handleRequestBack = () => goBackTo("death-certificate");
  const handleDeathCertContinue = (hasUploads: boolean) => {
    markStep.mutate({ taskId: INTAKE_TASK_IDS.DEATH_CERT, status: "completed" });
    if (hasUploads && estateId) {
      setStep("scanning");
    } else {
      markStep.mutate({ taskId: INTAKE_TASK_IDS.INTRO, status: "completed" });
      completeTriage("death_certificate_requested");
    }
  };

  const handleQrFileUploaded = useCallback(
    (files: { fileName: string }[]) => {
      trackEvent(EventType.DEATH_CERTIFICATE_UPLOADED, {
        estate_id: analyticsEstateId,
        upload_method: "phone",
        file_count: files.length,
        file_type: getFileTypes(files.map((f) => f.fileName)),
      });
      setPersonProperties({}, { death_certificate_upload_method: "phone" });
      uploadMethodRef.current = uploadMethodRef.current ?? "phone";
      refetchDeathCerts();
      setStep("death-certificate");
    },
    [refetchDeathCerts, analyticsEstateId],
  );

  const handleQrSessionExpired = useCallback(() => {
    trackEvent(EventType.MOBILE_UPLOAD_SESSION_EXPIRED, { estate_id: analyticsEstateId });
  }, [analyticsEstateId]);

  const handleRequestSent = useCallback(
    (recipientName: string, recipientRelationship: string | null) => {
      trackEvent(EventType.DEATH_CERTIFICATE_REQUESTED, {
        estate_id: analyticsEstateId,
        recipient_relationship: recipientRelationship ?? undefined,
      });
      const hasExistingDocs = (existingDeathCerts ?? []).length > 0;
      if (!hasExistingDocs) {
        markStep.mutate({
          taskId: INTAKE_TASK_IDS.DEATH_CERT,
          status: "in_progress",
          answers: { requested_from: recipientName },
        });
      }
      setRequestedFrom(recipientName);
      setStep("death-certificate");
    },
    [analyticsEstateId, markStep, existingDeathCerts],
  );

  const handleUploadCompleted = useCallback((upload: CompletedUpload) => {
    uploadMethodRef.current = uploadMethodRef.current ?? "computer";
    setSessionUploads((prev) => [...prev, upload]);
  }, []);

  const deleteDocument = useDocumentDelete();

  const handleUploadRemoved = useCallback(
    (fileName: string, documentId?: number) => {
      setSessionUploads((prev) => prev.filter((u) => u.fileName !== fileName));
      if (documentId) {
        setRemovedDocIds((prev) => new Set(prev).add(documentId));
        deleteDocument.mutate(documentId, {
          onSuccess: () => refetchDeathCerts(),
        });
      } else {
        setRemovedSessionFileNames((prev) => new Set(prev).add(fileName));
      }
    },
    [refetchDeathCerts, deleteDocument],
  );

  const isFullWidthStep = step !== "welcome";

  return (
    <Dialog open={open && isReady}>
      <DialogContent
        size={isFullWidthStep ? "flow" : "xl"}
        className={cn(isFullWidthStep ? "!p-0 !gap-0 overflow-hidden" : "sm:max-h-[90dvh]")}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          Welcome to Elayne: confirm your legal authority
        </DialogTitle>

        {handleDismiss && step !== "processing" && step !== "scanning" && (
          <IconButton
            className="absolute right-4 top-4 z-10"
            onClick={handleDismiss}
            label="Close"
            tooltip={false}
          >
            <X />
          </IconButton>
        )}

        {step === "processing" && estateId ? (
          <TriageModalProcessingStep
            estateId={estateId}
            onComplete={() => completeTriage("full")}
          />
        ) : step === "invite" && estateId && inviteQueue[inviteIndex] ? (
          <TriageModalInviteStep
            key={inviteQueue[inviteIndex].inviteRole}
            personFirstName={personFirstName}
            estateId={estateId}
            inviteeName={inviteQueue[inviteIndex].inviteeName}
            inviteRole={inviteQueue[inviteIndex].inviteRole}
            onBack={() => {
              const prev = findUnsentIndex(inviteQueue, inviteIndex - 1, -1);
              if (prev >= 0) setInviteIndex(prev);
              else goBackTo("authority");
            }}
            onContinue={() => {
              sentInviteNames.current.add(inviteKey(inviteQueue[inviteIndex]));
              const next = findUnsentIndex(inviteQueue, inviteIndex + 1, 1);
              if (next >= 0) {
                setInviteIndex(next);
              } else {
                markStep.mutate({ taskId: INTAKE_TASK_IDS.INVITES, status: "completed" });
                setStep("processing");
              }
            }}
          />
        ) : step === "authority" && estateId ? (
          <TriageModalAuthorityStep
            personFirstName={personFirstName}
            estateId={estateId}
            onBack={() => goBackTo("confirm-details")}
            onContinue={(authorityBucket) => {
              authorityBucketRef.current = authorityBucket;
              markStep.mutate({ taskId: INTAKE_TASK_IDS.AUTHORITY, status: "completed" });
              setStep("processing");
            }}
            onInvite={(data, authorityBucket) => {
              authorityBucketRef.current = authorityBucket;
              markStep.mutate({ taskId: INTAKE_TASK_IDS.AUTHORITY, status: "completed" });
              const deduped = Array.from(new Map(data.map((d) => [inviteKey(d), d])).values());
              setInviteQueue(deduped);
              const firstUnsent = findUnsentIndex(deduped, 0, 1);
              if (firstUnsent >= 0) {
                setInviteIndex(firstUnsent);
                setStep("invite");
              } else {
                setStep("processing");
              }
            }}
          />
        ) : step === "confirm-details" && estateId ? (
          <TriageModalConfirmDetailsStep
            personFirstName={personFirstName}
            estateId={estateId}
            onBack={() => goBackTo("death-certificate")}
            onContinue={(result: ConfirmDetailsResult) => {
              trackEvent(EventType.TRIAGE_DETAILS_CONFIRMED, {
                estate_id: analyticsEstateId,
                extraction_result: extractionResultRef.current,
                fields_edited: result.fieldsEdited.join(","),
                edited_field_count: result.fieldsEdited.length,
                save_failed: result.saveFailed,
              });
              markStep.mutate({ taskId: INTAKE_TASK_IDS.CONFIRM_DETAILS, status: "completed" });
              setStep("authority");
            }}
          />
        ) : step === "scanning" && estateId ? (
          <TriageModalScanningStep
            personFirstName={personFirstName}
            estateId={estateId}
            documentIds={sessionUploads
              .map((u) => u.documentId)
              .filter((id): id is number => id != null)}
            onExtractionResult={(result) => {
              extractionResultRef.current = result;
            }}
            onComplete={() => {
              markStep.mutate({ taskId: INTAKE_TASK_IDS.CONFIRM_DETAILS, status: "in_progress" });
              queryClient.invalidateQueries({ queryKey: ["deathCertExtraction", estateId] });
              queryClient.invalidateQueries({ queryKey: ["deceasedUserData"] });
              queryClient.invalidateQueries({ queryKey: ["deceasedSSNData"] });
              queryClient.invalidateQueries({ queryKey: ["userContacts"] });
              invalidateUploadedDocumentQueries(queryClient, estateId);
              setStep("confirm-details");
            }}
          />
        ) : step === "qr" ? (
          <TriageModalQrStep
            title="Add the death certificate"
            description={`One upload confirms ${personFirstName || "the person who passed"}'s details and sets up the right steps for the estate. It's saved for anywhere else that asks.`}
            sidebar={<TriageModalSidebar activeStep={2} />}
            documentType="death_certificate"
            onBack={handleQrBack}
            onFileUploaded={handleQrFileUploaded}
            onSessionExpired={handleQrSessionExpired}
          />
        ) : step === "request" ? (
          <TriageModalRequestStep
            title="Add the death certificate"
            description={`One upload confirms ${personFirstName || "the person who passed"}'s details and sets up the right steps for the estate. It's saved for anywhere else that asks.`}
            sidebar={<TriageModalSidebar activeStep={2} />}
            documentType="death_certificate"
            documentLabel="Death certificate"
            personFirstName={personFirstName}
            onBack={handleRequestBack}
            onRequestSent={handleRequestSent}
          />
        ) : step === "death-certificate" ? (
          <TriageModalDeathCertificateStep
            personFirstName={personFirstName}
            onBack={handleBack}
            onContinue={handleDeathCertContinue}
            onPhoneUpload={handlePhoneUpload}
            onRequestUpload={handleRequestUpload}
            completedUploads={completedUploads}
            onUploadCompleted={handleUploadCompleted}
            onUploadRemoved={handleUploadRemoved}
            requestedFrom={requestedFrom}
          />
        ) : (
          <>
            <div className="-mx-6 -mt-6 px-6 pt-8 flex-1 min-h-0 overflow-y-auto">
              <TriageModalStepperStep personFirstName={personFirstName} userFirstName={firstName} />
            </div>

            <div className="flex items-center justify-end shrink-0">
              <Button variant="brand" size="md" rounded onClick={handleStart}>
                Start
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
